import React, { useState } from 'react';
import { toCommaSeperatedString, toCurrency, toFormattedRate, toFormattedDate } from '@app/utilities';
import { Segment } from 'semantic-ui-react';
import { useCancelInstalmentPlan, useGetInstalmentPlans, useReinstateInstalmentPlan } from '@app/sdk';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { SegmentWithHeader, Table } from '@app/shared';
import { useAccountIdContext } from '../AccountIdContext';
import Modal from 'react-overlays/Modal';
import styled from 'styled-components';
import { InstalmentPlan } from "@app/models";

const Backdrop = styled('div')`
    position: fixed;
    z-index: 1040;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #000;
    opacity: 0.25;
`;

const ReinstateModal = styled(Modal)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: fit-content;
    z-index: 1040;
    background-color: white;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
`;

export default function InstalmentPlans() {
  const { accountId } = useAccountIdContext();

  const { data: instalmentPlans, isLoading } = useGetInstalmentPlans(accountId);

  const instalmentPlanColumnDef: ColDef[] = [
    {
      field: 'subledgerId',
      headerName: 'Actions',
      minWidth: 250,
      cellRenderer: ActionButtons
    },
    {
      field: 'name',
      headerName: 'Description',
      valueFormatter: (params) => `${params.data.name}`,
    },
    { field: 'interestRate', headerName: 'Rate', valueFormatter: (params) => toFormattedRate(params?.value) },
    {
      field: 'startDate',
      headerName: 'Start Date',
      valueFormatter: (params) => toFormattedDate(params?.value),
    },
    { field: 'currentTerm', headerName: 'Current Term' },
    { field: 'elapsedTerm', headerName: 'Elapsed Term' },
    { field: 'plannedRemainingTerm', headerName: 'Planned Remaining Term' },
    { field: 'remainingTerm', headerName: 'Remaining Term' },
    { field: 'endDate', headerName: 'End Date', valueFormatter: (params) => toFormattedDate(params?.value) },
    {
      field: 'transactionAmount.value',
      headerName: 'Transaction Amount',
      valueFormatter: (params) => toCurrency(params?.value),
    },
    {
      field: 'instalmentAmount.value',
      headerName: 'Instalment Amount',
      valueFormatter: (params) => toCurrency(params?.value),
    },
    {
      field: 'instalmentAmountDue.value',
      headerName: 'Instalment Amount Due',
      valueFormatter: (params) => toCurrency(params?.value),
    },
    {
      field: 'nextInstalmentAmount.value',
      headerName: 'Next Instalment Amount',
      valueFormatter: (params) => toCurrency(params?.value),
    },
    {
      field: 'totalInterestPayable.value',
      headerName: 'Total Interest Payable',
      valueFormatter: (params) => toCurrency(params?.value),
    },
    {
      field: 'totalRepayableAmount.value',
      headerName: 'Total Repayable',
      valueFormatter: (params) => toCurrency(params?.value),
    },
    { field: 'status', headerName: 'Status' },
    { field: 'planType', headerName: 'Plan Type ' },
    {
      field: 'originalSubldgerIds',
      headerName: 'Original Subledger Ids',
      valueFormatter: (params) => toCommaSeperatedString(params?.value),
      wrapText: true,
      editable: true,
      cellEditor: 'agTextCellEditor',

    }
  ];

  return (
    <Segment style={{ height: '550px' }}>
      <h2>Instalment Plans</h2>
      {isLoading && <div className="ui active centered inline loader" />}
      {instalmentPlans && (
        <div style={{ height: '450px' }}>
          <Table items={instalmentPlans} columnDefs={instalmentPlanColumnDef} />
        </div>
      )}
    </Segment>
  );
}

function ActionButtons({ data, value }: ICellRendererParams<InstalmentPlan, string>) {
  const { mutateAsync: cancelInstalmentPlan, isPending: isCancellationPending } = useCancelInstalmentPlan();
  const { mutateAsync: reinstateInstalmentPlan, isPending: isReinstatementPending } = useReinstateInstalmentPlan();
  const [popupVisible, setPopupVisible] = useState(false);
  const renderBackdrop = (props) => <Backdrop {...props} />;
  const { accountId } = useAccountIdContext();
  const [instalmentAmount, setInstalmentAmount] = useState<number | null>(null);
  const [term, setTerm] = useState<number | null>(null);
  const isCancellationDisabled = data.status == "Cancelled" || data.planType == "PayDown";

  const onCancelClicked = async () => {
    cancelInstalmentPlan({ accountId: accountId, subledgerId: value });
  };

  const onReinstateSubmitClicked = async () => {
    await reinstateInstalmentPlan({ accountId: accountId, subledgerId: value, instalmentAmount: instalmentAmount, term: term });
    setPopupVisible(false);
  };

  const onReinstateClicked = () => {
    setPopupVisible(true);
  };

  const closePopup = () => {
    setPopupVisible(false);
  };

  return (
    <>
      <button className={'ui button'} onClick={() => onCancelClicked()} disabled={isCancellationDisabled}>
        Cancel {isCancellationPending && <span className="ui active centered inline loader" />}
      </button>
      <button className={'ui button'} onClick={() => onReinstateClicked()} disabled={data.status != "Cancelled"}>
        Reinstate {isReinstatementPending && <span className="ui active centered inline loader" />}
      </button>

      <ReinstateModal show={popupVisible} aria-labelledby="modal-label" renderBackdrop={renderBackdrop}>
        <SegmentWithHeader header={`Plan Reinstatement`} onClose={closePopup} className={'basic'}>
          <div className="content">
            <div className="ui card">
              <div className="content">
                <h3>Cancelled plan information</h3>
                <p>Original term: {data.originalTerm}</p>
              </div>
            </div>

            <div className="ui card">
              <div className="content">
                <div className="ui form">
                  <h3>Reinstatement request</h3>
                  <div className="field">
                    <label>Instalment amount</label>
                    <div className="ui labeled input">
                      <div className="ui label">£</div>
                      <input
                        type="number"
                        placeholder="Instalment amount"
                        step=".01"
                        onChange={(e) => setInstalmentAmount(parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>Term</label>
                    <div className="ui labeled input">
                      <input
                        type="number"
                        placeholder="Term"
                        step="1"
                        onChange={(e) => setTerm(parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <br />
          <button type="button"
            className={`ui primary button`}
            onClick={() => onReinstateSubmitClicked()}
            disabled={isReinstatementPending}>
            Submit
          </button>
        </SegmentWithHeader>
      </ReinstateModal>
    </>
  );
}
